<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header -->
    <header class="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-40">
      <div class="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
        <div class="flex items-center gap-2.5">
          <div class="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
            <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
          </div>
          <span class="text-xl font-bold text-gray-900 tracking-tight">FileShare</span>
        </div>

        <!-- Auth area -->
        <div v-if="currentUser" class="flex items-center gap-3">
          <span class="text-sm text-gray-600 hidden sm:block">{{ currentUser.name || currentUser.username }}</span>
          <button
            class="text-sm text-gray-500 hover:text-red-500 transition-colors"
            @click="logout"
          >
            ログアウト
          </button>
        </div>
        <button
          v-else
          class="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          @click="loginWithGitLab"
        >
          <!-- GitLab fox icon (simplified) -->
          <svg class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M22.65 14.39L12 22.13 1.35 14.39a.84.84 0 01-.3-.94l1.22-3.78 2.44-7.51A.42.42 0 014.82 2a.43.43 0 01.58 0 .42.42 0 01.11.18l2.44 7.49h8.1l2.44-7.49a.42.42 0 01.11-.18.43.43 0 01.58 0 .42.42 0 01.11.18l2.44 7.51L23 13.45a.84.84 0 01-.35.94z"/>
          </svg>
          GitLab でログイン
        </button>
      </div>
    </header>

    <!-- Main Content -->
    <main class="max-w-4xl mx-auto px-4 py-8 space-y-6">

      <!-- Not logged in state -->
      <div v-if="!currentUser" class="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
        <div class="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <svg class="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <h2 class="text-xl font-semibold text-gray-900 mb-2">ログインが必要です</h2>
        <p class="text-gray-500 text-sm mb-6">GitLab アカウントでログインしてファイルをアップロード・管理してください</p>
        <button
          class="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-medium px-6 py-3 rounded-xl transition-colors"
          @click="loginWithGitLab"
        >
          <svg class="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M22.65 14.39L12 22.13 1.35 14.39a.84.84 0 01-.3-.94l1.22-3.78 2.44-7.51A.42.42 0 014.82 2a.43.43 0 01.58 0 .42.42 0 01.11.18l2.44 7.49h8.1l2.44-7.49a.42.42 0 01.11-.18.43.43 0 01.58 0 .42.42 0 01.11.18l2.44 7.51L23 13.45a.84.84 0 01-.35.94z"/>
          </svg>
          GitLab でログイン
        </button>
      </div>

      <!-- Logged-in state -->
      <template v-else>
        <!-- Hero + Quota -->
        <div class="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-6 text-white shadow-lg">
          <div class="flex items-start justify-between mb-4">
            <div>
              <h1 class="text-2xl font-bold mb-1">ようこそ、{{ currentUser.name || currentUser.username }}</h1>
              <p class="text-blue-100 text-sm">ファイルをアップロードして共有リンクを発行できます。共有ファイルは 7 日後に自動削除されます。</p>
            </div>
          </div>
          <!-- Storage quota bar -->
          <div>
            <div class="flex justify-between text-xs text-blue-100 mb-1.5">
              <span>ストレージ使用量</span>
              <span>{{ formatSize(currentUser.usedStorage) }} / {{ formatSize(currentUser.storageQuota) }}</span>
            </div>
            <div class="bg-blue-500 rounded-full h-2">
              <div
                class="h-2 rounded-full transition-all duration-500"
                :class="quotaPercent >= 90 ? 'bg-red-400' : quotaPercent >= 70 ? 'bg-yellow-300' : 'bg-white'"
                :style="{ width: `${Math.min(quotaPercent, 100)}%` }"
              ></div>
            </div>
            <p v-if="quotaPercent >= 90" class="text-xs text-red-200 mt-1">
              ストレージが残り少なくなっています
            </p>
          </div>
        </div>

        <!-- Upload Section -->
        <FileUpload :user="currentUser" @uploaded="onFileUploaded" />

        <!-- File List Section -->
        <FileList
          :files="files"
          :is-loading="isLoadingFiles"
          :error-message="listError"
          @refresh="loadFiles"
          @deleted="onFileDeleted"
        />
      </template>
    </main>

    <!-- Toast -->
    <Transition name="toast">
      <div
        v-if="toastMessage"
        class="fixed bottom-6 right-6 z-50 bg-gray-900 text-white px-5 py-3 rounded-xl shadow-xl flex items-center gap-3 max-w-sm"
      >
        <svg class="w-5 h-5 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
        </svg>
        <p class="text-sm font-medium flex-1 min-w-0">{{ toastMessage }}</p>
        <button class="text-gray-400 hover:text-white" @click="toastMessage = ''">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </Transition>

    <!-- Footer -->
    <footer class="max-w-4xl mx-auto px-4 py-6 text-center text-xs text-gray-400">
      <p>FileShare — Vue 3 · AWS S3 · API Gateway · DynamoDB · Lambda</p>
    </footer>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import FileUpload from './components/FileUpload.vue';
import FileList from './components/FileList.vue';
import { listFiles, getCurrentUser } from './api.js';

const GITLAB_URL = import.meta.env.VITE_GITLAB_URL || '';
const GITLAB_CLIENT_ID = import.meta.env.VITE_GITLAB_CLIENT_ID || '';

// ── State ─────────────────────────────────────────────────────────────────────
const currentUser = ref(null);
const files = ref([]);
const isLoadingFiles = ref(false);
const listError = ref('');
const toastMessage = ref('');
let toastTimer = null;

// ── Computed ──────────────────────────────────────────────────────────────────
const quotaPercent = computed(() => {
  if (!currentUser.value?.storageQuota) return 0;
  return Math.round((currentUser.value.usedStorage / currentUser.value.storageQuota) * 100);
});

// ── Auth ──────────────────────────────────────────────────────────────────────
function loginWithGitLab() {
  const redirectUri = `${window.location.origin}/auth/callback`;
  const params = new URLSearchParams({
    client_id: GITLAB_CLIENT_ID,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: 'read_user',
  });
  window.location.href = `${GITLAB_URL}/oauth/authorize?${params}`;
}

function logout() {
  localStorage.removeItem('sessionToken');
  localStorage.removeItem('currentUser');
  currentUser.value = null;
  files.value = [];
}

async function refreshCurrentUser() {
  try {
    const user = await getCurrentUser();
    currentUser.value = user;
    localStorage.setItem('currentUser', JSON.stringify(user));
  } catch (err) {
    if (err.status === 401) logout();
  }
}

// ── Files ─────────────────────────────────────────────────────────────────────
async function loadFiles() {
  if (!currentUser.value) return;
  isLoadingFiles.value = true;
  listError.value = '';
  try {
    const data = await listFiles();
    files.value = data.files || [];
  } catch (err) {
    if (err.status === 401) { logout(); return; }
    listError.value = err.message || 'ファイルの読み込みに失敗しました';
  } finally {
    isLoadingFiles.value = false;
  }
}

function showToast(message) {
  toastMessage.value = message;
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => { toastMessage.value = ''; }, 4000);
}

function onFileUploaded({ fileName }) {
  showToast(`"${fileName}" をアップロードしました`);
  refreshCurrentUser();
  loadFiles();
}

function onFileDeleted(fileId) {
  files.value = files.value.filter((f) => f.fileId !== fileId);
  showToast('ファイルを削除しました');
  refreshCurrentUser();
}

// ── Utils ─────────────────────────────────────────────────────────────────────
function formatSize(bytes) {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let value = bytes;
  while (value >= 1024 && i < units.length - 1) { value /= 1024; i++; }
  return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

// ── Init ──────────────────────────────────────────────────────────────────────
onMounted(async () => {
  const stored = localStorage.getItem('currentUser');
  const token = localStorage.getItem('sessionToken');
  if (stored && token) {
    currentUser.value = JSON.parse(stored);
    // Refresh from server to get latest quota
    await refreshCurrentUser();
    await loadFiles();
  }
});
</script>

<style>
.toast-enter-active, .toast-leave-active { transition: all 0.3s ease; }
.toast-enter-from { opacity: 0; transform: translateY(1rem); }
.toast-leave-to { opacity: 0; transform: translateX(100%); }
</style>
